"use client";

import { useNextRouterContext } from "@faire/retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { useMaybeAssignCategoryTreeExperiment } from "@faire/retailer-visitor-shared/lib/useMaybeAssignCategoryTreeExperiment";
import { useSettingNextJSRscCategoryPage } from "@faire/retailer-visitor-shared/settings/useSettingNextJSRscCategoryPage";
import { useNavigationQuery } from "@faire/retailer-visitor-shared/stores/domain/useNavigationQuery";
import { IRecordCategoryNodesVisibilityRequest } from "@faire/web-api--source/indigofair/category/IRecordCategoryNodesVisibilityRequest";
import { ClientPlatformType } from "@faire/web-api--source/indigofair/data/ClientPlatformType";
import { INavigationLink as INavigationLinkData } from "@faire/web-api--source/indigofair/data/INavigationLink";
import { route as categoryC1Route } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import flattenDeep from "lodash/flattenDeep";
import uniq from "lodash/uniq";
import { useCallback, useEffect, useMemo } from "react";

import {
  IL1NavigationLink,
  IL2NavigationLink,
} from "./__internal__/ILNavigationLink";
import {
  getNavigationBarDropdownLinks,
  getNavigationBarVisibleLinks,
} from "./__internal__/navigationQueryUtils";
import { NavigationLinksBar } from "./NavigationLinksBar";

export const LoggedInNavigationLinksBar: React.FC = () => {
  const { isDesktopAndAbove } = useViewport();
  const maybeAssignCategoryTreeExperiment =
    useMaybeAssignCategoryTreeExperiment();

  const { navigationLinkGroup } = useNavigationQuery(
    ClientPlatformType.CLIENT_DESKTOP
  );
  const visibleLinks = useMemo(
    () => getNavigationBarVisibleLinks(navigationLinkGroup),
    [navigationLinkGroup]
  );

  const dropdownLinks = useMemo(
    () => getNavigationBarDropdownLinks(navigationLinkGroup),
    [navigationLinkGroup]
  );

  const categoryPathIdentifiersForAssignment = useMemo(() => {
    const invisibleForAssignmentCategoryPathIdentifiers = navigationLinkGroup
      .flatMap((group) => group.navigation_links)
      .filter(
        (link) =>
          link.style_group ===
          INavigationLinkData.StyleGroup.INVISIBLE_FOR_ASSIGNMENT
      )
      .map((navigationLink) => navigationLink.path_identifier);

    const visibleCategoryPathIdentifiers = visibleLinks.map(
      (link) => link.categoryPathIdentifier
    );

    return [
      ...invisibleForAssignmentCategoryPathIdentifiers,
      ...visibleCategoryPathIdentifiers,
    ];
  }, [navigationLinkGroup, visibleLinks]);

  useEffect(() => {
    if (isDesktopAndAbove) {
      maybeAssignCategoryTreeExperiment(
        () => categoryPathIdentifiersForAssignment,
        IRecordCategoryNodesVisibilityRequest.Source.WEB_DESKTOP_TOP_NAV
      );
    }
  }, [
    isDesktopAndAbove,
    categoryPathIdentifiersForAssignment,
    maybeAssignCategoryTreeExperiment,
  ]);

  const isRSCCategoryPageEnabled = useSettingNextJSRscCategoryPage();
  const { prefetch: nextPrefetch } = useNextRouterContext();

  const nextPrefetchCategoryC1Route = useCallback(() => {
    if (!isRSCCategoryPageEnabled) {
      nextPrefetch(categoryC1Route);
    }
  }, [isRSCCategoryPageEnabled, nextPrefetch]);

  const maybeAssignCategoryTreeExperimentForDropdown = useCallback(
    (
      link: IL1NavigationLink,
      selectedL2Link: IL2NavigationLink | undefined
    ) => {
      maybeAssignCategoryTreeExperiment(() => {
        const allVisibleCategoryPathIdentifiers = uniq(
          flattenDeep([
            link.categoryPathIdentifier,
            ...link.l2Links.map((l2Link) => [
              l2Link.categoryPathIdentifier,
              ...(l2Link.name === selectedL2Link?.name
                ? l2Link.l3LinkGroups.map((l3Links) =>
                    l3Links.l3Links.map(
                      (l3Link) => l3Link.categoryPathIdentifier
                    )
                  )
                : [undefined]),
            ]),
          ]).filter(isNotUndefined)
        );

        const l2CategoryPathIdentifiers =
          link.invisibleForAssignmentChildCategoryPathIdentifiers;
        const l3CategoryPathIdentifiers =
          selectedL2Link?.l3LinkGroups.flatMap(
            (l3LinkGroup) =>
              l3LinkGroup.invisibleForAssignmentL3CategoryPathIdentifiers
          ) ?? [];

        const invisibleForAssignmentCategoryPathIdentifiers = uniq([
          ...l2CategoryPathIdentifiers,
          ...l3CategoryPathIdentifiers,
        ]);

        return [
          ...allVisibleCategoryPathIdentifiers,
          ...invisibleForAssignmentCategoryPathIdentifiers,
        ];
      }, IRecordCategoryNodesVisibilityRequest.Source.WEB_DESKTOP_TOP_NAV_DROPDOWN);
    },
    [maybeAssignCategoryTreeExperiment]
  );

  return (
    <NavigationLinksBar
      nextPrefetchCategoryC1Route={nextPrefetchCategoryC1Route}
      maybeAssignCategoryTreeExperimentForDropdown={
        maybeAssignCategoryTreeExperimentForDropdown
      }
      visibleLinks={visibleLinks}
      dropdownLinks={dropdownLinks}
    />
  );
};
